import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, database } from '../../firebaseConfig.js';
import Layout from '../Layout';
import Loader from '../Loader';
import Quiz from '../Quiz';
import Result from '../Result';
import SEO from '../SEO';
import { shuffle } from '../../utils';
import mockTestConfig from '../../config/mockTestConfig.json';
import { saveQuizResult } from '../../utils/quizResults';

const MockTest = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState(null);
  const [data, setData] = useState(null);
  const [countdownTime, setCountdownTime] = useState(null);
  const [isQuizStarted, setIsQuizStarted] = useState(false);
  const [isQuizCompleted, setIsQuizCompleted] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [attemptedQuestions, setAttemptedQuestions] = useState([]);
  const [mockTestName, setMockTestName] = useState('Mock Test');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        await loadMockTest(currentUser);
      } else {
        setError('Please log in to start a mock test.');
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const loadMockTest = async (currentUser) => {
    try {
      setLoading(true);
      setLoadingMessage({
        title: 'Loading your mock test...',
        message: 'Preparing questions based on your profile',
      });

      // Get user profile to determine which mock test to load
      const userRef = doc(database, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        setError('User profile not found. Please complete your profile first.');
        setLoading(false);
        return;
      }

      const userData = userDoc.data();
      const preparingFor = userData.personalInfo?.preparingFor;

      if (!preparingFor) {
        setError('Please select your field of study in your profile before starting a mock test.');
        setLoading(false);
        return;
      }

      // Get mock test config based on user's preparingFor field
      const mockTestConfigData = mockTestConfig[preparingFor];

      if (!mockTestConfigData) {
        setError(`No mock test configuration found for: ${preparingFor}. Please contact support.`);
        setLoading(false);
        return;
      }

      if (!mockTestConfigData.mockTestId) {
        setError(`Mock test ID not configured for: ${preparingFor}. Please contact support.`);
        setLoading(false);
        return;
      }

      // Store mock test name for saving results
      setMockTestName(mockTestConfigData.name || preparingFor || 'Mock Test');

      // Get attempted questions
      const attemptedQuestionIds = userData.attempted_questions || [];
      setAttemptedQuestions(attemptedQuestionIds);

      // Fetch mock test document from "Mock Tests" collection
      const mockTestRef = doc(database, 'Mock Tests', mockTestConfigData.mockTestId);
      const mockTestDoc = await getDoc(mockTestRef);

      if (!mockTestDoc.exists()) {
        setError(`Mock test document not found: ${mockTestConfigData.mockTestId}. Please contact support.`);
        setLoading(false);
        return;
      }

      const mockTestData = mockTestDoc.data();

      // Parse questions from the mock test document
      // Support multiple possible field names: questions, mcqs, items, data, list
      let mockQuestions = [];
      if (Array.isArray(mockTestData.questions)) {
        mockQuestions = mockTestData.questions;
      } else if (Array.isArray(mockTestData.mcqs)) {
        mockQuestions = mockTestData.mcqs;
      } else if (Array.isArray(mockTestData.items)) {
        mockQuestions = mockTestData.items;
      } else if (Array.isArray(mockTestData.data)) {
        mockQuestions = mockTestData.data;
      } else if (Array.isArray(mockTestData.list)) {
        mockQuestions = mockTestData.list;
      } else if (mockTestData.questions && typeof mockTestData.questions === 'object') {
        mockQuestions = Object.values(mockTestData.questions);
      } else if (mockTestData.mcqs && typeof mockTestData.mcqs === 'object') {
        mockQuestions = Object.values(mockTestData.mcqs);
      }

      if (!mockQuestions.length) {
        setError('This mock test does not have any questions yet.');
        setLoading(false);
        return;
      }

      // Process and normalize questions
      const requestedQuestionCount = mockTestConfigData.questionCount || mockQuestions.length;
      const desiredCount = requestedQuestionCount > 0 ? requestedQuestionCount : mockQuestions.length;

      if (desiredCount > mockQuestions.length) {
        setError(
          `Only ${mockQuestions.length} questions are available in this mock test, but ${desiredCount} are requested.`
        );
        setLoading(false);
        return;
      }

      // Filter out attempted questions and select random ones
      const unattemptedQuestions = mockQuestions.filter(
        (question) => {
          const questionId = question.id || question.questionId;
          return questionId && !attemptedQuestionIds.includes(String(questionId));
        }
      );

      if (unattemptedQuestions.length < desiredCount) {
        setError(
          `Not enough unattempted questions available. Found ${unattemptedQuestions.length} unattempted questions, but ${desiredCount} are required.`
        );
        setLoading(false);
        return;
      }

      // Select random questions
      const selectedQuestions = shuffle(unattemptedQuestions).slice(0, desiredCount);

      // Normalize and enhance questions
      const enhancedQuestions = selectedQuestions.map((question, index) => {
        const correctAnswer =
          question.correct_answer ||
          question.correctAnswer ||
          question.answer ||
          question.correct;

        if (!correctAnswer) {
          throw new Error('One of the mock test questions is missing a correct answer.');
        }

        let incorrectAnswers = [];
        if (Array.isArray(question.incorrect_answers)) {
          incorrectAnswers = question.incorrect_answers;
        } else if (Array.isArray(question.incorrectAnswers)) {
          incorrectAnswers = question.incorrectAnswers;
        } else if (Array.isArray(question.incorrects)) {
          incorrectAnswers = question.incorrects;
        }

        if ((!incorrectAnswers || incorrectAnswers.length === 0) && Array.isArray(question.options) && correctAnswer) {
          incorrectAnswers = question.options.filter((option) => option !== correctAnswer);
        }

        const normalizedIncorrects = Array.from(
          new Set(
            (incorrectAnswers || [])
              .filter((option) => option && option !== correctAnswer)
              .map((option) => (typeof option === 'string' ? option.trim() : option))
          )
        );

        return {
          ...question,
          id: question.id || question.questionId || `mock-test-${index}`,
          category: question.category || mockTestConfigData.category || 'Mock Test',
          difficulty: question.difficulty || 'medium',
          correct_answer: correctAnswer,
          incorrect_answers: normalizedIncorrects,
          solution: question.solution || question.explanation || '',
          options: shuffle([correctAnswer, ...normalizedIncorrects]),
        };
      });

      setData(enhancedQuestions);
      setCountdownTime(mockTestConfigData.timeLimitSeconds);
      setIsQuizStarted(true);
      setLoading(false);
    } catch (err) {
      console.error('Error loading mock test:', err);
      setError('Failed to load mock test. Please try again.');
      setLoading(false);
    }
  };

  const endQuiz = async (resultData) => {
    setLoading(true);
    setLoadingMessage({
      title: 'Saving your results...',
      message: 'Just a moment!',
    });

    // Update attempted questions and save quiz results
    if (user && data) {
      try {
        const newAttemptedIds = data.map((q) => q.id);
        const updatedAttemptedQuestions = Array.from(
          new Set([...attemptedQuestions, ...newAttemptedIds])
        );

        const userRef = doc(database, 'users', user.uid);
        await updateDoc(userRef, {
          attempted_questions: updatedAttemptedQuestions,
        });

        // Save quiz result to user's history
        await saveQuizResult(user.uid, resultData, 'mock', mockTestName);
      } catch (err) {
        console.error('Error updating attempted questions:', err);
      }
    }

    setTimeout(() => {
      setIsQuizStarted(false);
      setIsQuizCompleted(true);
      setResultData(resultData);
      setLoading(false);
    }, 2000);
  };

  const replayQuiz = () => {
    setLoading(true);
    setLoadingMessage({
      title: 'Getting ready for round two.',
      message: "It won't take long!",
    });

    const shuffledData = shuffle(data);
    shuffledData.forEach((element) => {
      element.options = shuffle(element.options);
    });

    setData(shuffledData);

    setTimeout(() => {
      setIsQuizStarted(true);
      setIsQuizCompleted(false);
      setResultData(null);
      setLoading(false);
    }, 1000);
  };

  const resetQuiz = () => {
    setLoading(true);
    setLoadingMessage({
      title: 'Returning to home...',
      message: 'Thank you for taking the mock test!',
    });

    setTimeout(() => {
      navigate('/');
      setData(null);
      setCountdownTime(null);
      setIsQuizStarted(false);
      setIsQuizCompleted(false);
      setResultData(null);
      setLoading(false);
    }, 1000);
  };

  if (error) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="bg-white p-8 rounded-lg shadow-lg max-w-md">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
            <p className="text-gray-700 mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Return to Home
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  // Hide header only during active quiz taking
  const hideHeader = isQuizStarted && !isQuizCompleted;

  return (
    <>
      <SEO
        title="NUST Mock Test - Prepify | Practice NET Entrance Exam Online"
        description="Take full-length NUST Entry Test (NET) mock exams with Prepify. Practice with real exam format, timed tests, and detailed analytics. Perfect for NET Engineering, CS, and Business preparation."
        keywords="NUST Mock Test, NET Practice Test, NUST Online Test, NET Entrance Exam Practice, NUST Exam Simulator, NET Test Preparation"
        url="/mock-test"
      />
      <Layout hideHeaderOnQuiz={hideHeader}>
        {loading && <Loader {...loadingMessage} />}
        {!loading && isQuizStarted && (
          <Quiz data={data} countdownTime={countdownTime} endQuiz={endQuiz} />
        )}
        {!loading && isQuizCompleted && (
          <Result {...resultData} replayQuiz={replayQuiz} resetQuiz={resetQuiz} />
        )}
      </Layout>
    </>
  );
};

export default MockTest;
